import axios from 'axios'
import Vuex from 'vuex'
import Vue from 'vue'
import user from './modules/user'
import createPersistedState from 'vuex-persistedstate'
import ExmasForTensakuVisiter from '../utils/ExmasForTensakuVisiter'
import { examInfo } from '../utils/ExmasForTensakuVisiter/type'

Vue.use(Vuex)

interface State {
  currentPath: string,
  prevPath: string,
  contracts: Array<string>,
  texts: Array<string>
  contents: Array<string>
  pngs: Array<string>
  memberId: string | null   // まなび/PDFで使用する暗号化会員ID
  grade: any
  month: number | null
  year: number | null,
  movieFilename: string,
  currentDate: string | Date,
  isMemberPage: boolean,  // テスト提出の会員用ページへのアクセスの場合true
  submittingTestInfo: any,  // テスト一覧で提出を選択したテストデータ
  submitMemberId: string | null,  // テスト提出で使用する会員制御ID (会員IDではない)
  examInfo?: examInfo,
  newContracts: Record<string, any>,
  externalId: string | null,
}

const state = (): State => ({
  currentPath: '',
  prevPath: '',
  contracts: [],
  texts: [],
  contents: [],
  pngs: [],
  memberId: null,
  grade: { name: 0 },
  month: null,
  year: null,
  movieFilename: '',
  currentDate: '',
  isMemberPage: true,
  submittingTestInfo: null,
  submitMemberId: null,
  newContracts: {},
  externalId: null,
})

interface Context {
  state: State,
  getters: any,
  commit: (name: string, AxiosResponse: any) => void
  dispatch: (name: string, payload?: any) => any
}

interface Actions {
  [key: string]: (context: Context, payload?: any) => void
}

const actions: Actions = {
  async getCurrentDate({ commit }) {
    try {
      const { data } = await axios.get(`/api/v1/date`)
      commit('SET_CURRENT_DATE', { current_date: data.current_date } )
    } catch (e) {
      return { status: 'error', message: e }
    }
  },
  async getTexts({ state, commit }) {
    try {
      const { data } = await axios.get(`/api/v1/prints/${state.memberId}`)
      commit('SET_CONTRACTS', data.contracts)
      const prints = {}
      data.contracts.forEach((contract: { month: number; prints: string; release_time: string|null }) => {
          Object.assign(prints, {[contract.month]: {prints: contract.prints, release_time: contract.release_time}})
      })
      commit('SET_TEXTS', prints)
      commit('SET_GRADE', { grade: data.grade } )
    } catch (e) {
      return { status: 'error', message: e }
    }
  },
  async getContents({ state, commit }) {
    try {
      const { data } = await axios.get(`/api/v1/contents/${state.memberId}`)
      commit('SET_CONTRACTS', data.contracts)
      const contents = {}
      data.contracts.forEach((contract: { month: number; contents: string; release_time: string|null }) => {
        Object.assign(contents, {[contract.month]: {contents: contract.contents, release_time: contract.release_time}})
      })
      commit('SET_CONTENTS', contents)
      commit('SET_GRADE', { grade: data.grade } )
    } catch (e) {
      return { status: 'error', message: e }
    }
  },
  async getContracts({ state, commit }) {
    try {
      const { data } = await axios.get(`/api/v1/contracts/${state.externalId}`, { dataType: '' })

      commit('SET_NEW_CONTRACTS', data.new_contracts)
    } catch (e) {
      return { status: 'error', message: e }
    }
  },
  async getNewTexts({ state, commit }) {
    try {
      const { data } = await axios.get('/api/v1/new_prints',
        {
          params: {
            new_contracts: state.newContracts,
            grade_id: state.grade.name
          },
          dataType: ''
        })
      commit('SET_CONTRACTS', data.contracts)
      const prints = {}
      data.contracts.forEach((contract: { month: number; prints: string }) => {
        Object.assign(prints, {[contract.month]: {prints: contract.prints}})
      })
      commit('SET_TEXTS', prints)
    } catch (e) {
      return { status: 'error', message: e }
    }
  },
  async getNewContents({ state, commit }) {
    try {
      const { data } = await axios.get('/api/v1/new_contents',
        {
          params: {
            new_contracts: state.newContracts,
            grade_id: state.grade.name
          },
          dataType: ''
        })
      commit('SET_CONTRACTS', data.contracts)
      const contents = {}
      data.contracts.forEach((contract: { month: number; contents: string }) => {
        Object.assign(contents, {[contract.month]: {contents: contract.contents}})
      })
      commit('SET_CONTENTS', contents)
    } catch (e) {
      return { status: 'error', message: e }
    }
  },
  // テスト提出の会員ユーザ認証情報を設定
  saveSubmitTestMemberAuth({ state, commit }, payload: { submitMemberId: string }) {
    commit('SET_SUBMIT_MEMBER_ID', payload)
    commit('user/setLastOperatedDate', { lastOperatedDate: new Date })
    axios.defaults.headers.common['submit-member-id'] = payload.submitMemberId

    // 紛らわしいので非会員用情報は削除
    commit('user/login', {})
    delete axios.defaults.headers.common['access-token']
    delete axios.defaults.headers.common['client']
    delete axios.defaults.headers.common['uid']
  },

  // テスト提出の非会員ユーザ認証情報を設定
  saveSubmitTestVisiterAuth({ state, commit }, payload: { response: any }) {
    const res = payload.response

    // レスポンスヘッダーから認証情報を取得
    const auth = {
      accessToken: res.headers['access-token'],
      client:      res.headers['client'],
      uid:         res.headers['uid'],
    }
    // 認証情報とログイン情報をstoreに格納
    commit('user/login', auth)
    commit('user/setLastOperatedDate', { lastOperatedDate: new Date })
    commit('SET_EXAM_INFO', { examInfo: ExmasForTensakuVisiter[res.data.data.exam_id] })
    // axiosのヘッダーにログイン情報設定
    const authData = sessionStorage.getItem('conan-zemi')

    if (authData) {
      const strageData = JSON.parse(authData);
      axios.defaults.headers.common['access-token'] = strageData.user.accessToken;
      axios.defaults.headers.common['client']       = strageData.user.client;
      axios.defaults.headers.common['uid']          = strageData.user.uid;
    } else {
      axios.defaults.headers.common['access-token'] = res.headers['access-token'];
      axios.defaults.headers.common['client']       = res.headers['client'];
      axios.defaults.headers.common['uid']          = res.headers['uid'];
    }

    // 紛らわしいので会員用のログイン情報は削除
    Vue.$cookies.remove('submit_member_id')
    delete axios.defaults.headers.common['submit-member-id']
  },

  // 認証系の情報を削除 ただしcookieの会員IDだけは本体側のログアウト処理で使うので残す
  deleteAuth({ state, commit }) {
    commit('SET_MEMBERID', { memberId: null })
    commit('SET_SUBMIT_MEMBER_ID', {submitMemberId: null})
    commit('user/logout', null)
    Vue.$cookies.remove('submit_member_id')
    delete axios.defaults.headers.common['access-token']
    delete axios.defaults.headers.common['client']
    delete axios.defaults.headers.common['uid']
    delete axios.defaults.headers.common['submit-member-id']
  },
}

const mutations = {
  SET_CURRENT_PATH(state: State, payload: any) {
    state.currentPath = payload.path
  },
  SET_PREV_PATH(state: State, payload: any) {
    state.prevPath = payload.path
  },
  SET_CONTRACTS(state: State, payload: any) {
    state.contracts = payload
  },
  SET_TEXTS(state: State, payload: any) {
    state.texts = payload
  },
  SET_CONTENTS(state: State, payload: any) {
    state.contents = payload
  },
  SET_MEMBERID(state: State, payload: any) {
    state.memberId = payload.memberId
  },
  SET_GRADE(state: State, payload: any) {
    state.grade = payload.grade
  },
  SET_MONTH(state: State, payload: any) {
    state.month = payload.month
  },
  SET_YEAR(state: State, payload: any) {
    state.year = payload.year
  },
  SET_MOVIE_FILE_NAME(state: State, payload: any) {
    state.movieFilename = payload.filename
  },
  SET_CURRENT_DATE(state: State, payload: any) {
    state.currentDate = payload.current_date
  },
  SET_IS_MEMBER_PAGE(state: State, payload: any) {
    state.isMemberPage = payload.isMemberPage
  },
  SET_SUBMITTING_TEST_INFO(state: State, payload: any) {
    state.submittingTestInfo = payload
  },
  SET_SUBMIT_MEMBER_ID(state: State, payload: any) {
    state.submitMemberId = payload.submitMemberId
  },
  SET_EXAM_INFO(state: State, payload: any) {
    state.examInfo = payload.examInfo
  },
  SET_NEW_CONTRACTS(state: State, payload: any) {
    state.newContracts = payload
  },
  SET_EXTERNALID(state: State, payload: any) {
    state.externalId = payload
  }
}

export default new Vuex.Store({
  strict: process.env.NODE_ENV !== 'production',
  modules: {
    user
  },
  state,
  actions,
  mutations,
  // リロードしてもstoreの値を保持する（ログインの永続化）
  // paths内に記載したstateはsessionStrageに保存する
  plugins: [createPersistedState({
    key: 'conan-zemi',
    storage: window.sessionStorage
})],
})
